import { h, Host } from "@stencil/core";
import { getAuthClient } from "@trumpf/ux.utils";
import { debounce } from "../../utils/debounce";
import { createHash, getCodeChallenge, getCodeVerifier, getQueryString, getRandomString, getRPIframeSrcdoc, getUrlOrigin } from "./silent-auth-utils";
const TRUMPF_ISSUERS_PROD = ["https://identity.trumpf.com"];
const TRUMPF_ISSUERS_STAGE = ["https://identity-s.trumpf.com", "https://identity-t.trumpf.com"];
const TRUMPF_CALLBACK_URI_PROD = "https://cdn.trumpf.com/static/auth-callback.html";
const TRUMPF_CALLBACK_URI_STAGE = "https://cdn-q.trumpf.com/static/auth-callback.html";
export class SilentAuth {
    /* eslint-disable @typescript-eslint/naming-convention */
    _authClient;
    _authClientId;
    _authTokenData = null;
    // @ts-expect-error Check for typing
    _authIframe = null;
    _authScopes;
    _authCallbackHandler;
    _codeVerifier;
    // @ts-expect-error Check for typing
    _rpIframe = null;
    _rpIframeInterval;
    _sessionState;
    _checkSessionEnabled;
    _startCheckSessionIntervalTimeout;
    _uid;
    _refreshTokenTimeout;
    _authFailureTimeout;
    /* eslint-enable @typescript-eslint/naming-convention */
    _log = (msg) => {
        /* eslint-disable no-console */
        if (this.debug && typeof console !== "undefined" && typeof console.log === "function") {
            console.log(`[AUTH]: ${msg}`);
        }
        /* eslint-enable no-console */
    };
    constructor() {
        this.issuer = "https://identity.trumpf.com";
        this.clientId = undefined;
        this.clientName = undefined;
        this.scope = "oicd";
        this.redirectUri = undefined;
        this.failedTimeout = 10;
        this.debug = undefined;
        this.hash = undefined;
        this.active = undefined;
        this.loggedIn = undefined;
        this._checkSessionEnabled = /.trumpf.com$/.test(window.location.hostname);
    }
    /** The dom node */
    domElement;
    /** Emitted when the component is loaded to inform the delegated instance about scopes that may have changed */
    instanceAdded;
    /** Emitted when the component is activated */
    instanceActivated;
    /**
     * Emitted after the authentication flow was completed.
     * At this point either a token was fetched successfully and broadcasted to the `AuthClient`,
     * or silent authentication failed for some reason.
     */
    uxAuth;
    /** If this is the active instance, silent code flow has to be performed if scopes have changed */
    onClientAdded(event) {
        if (event.detail.hash === this.hash && this.active && this.loggedIn) {
            if (Array.isArray(this._authScopes) && this.collectScopes().toString() !== this._authScopes.toString()) {
                this.stopCheckSessionInterval();
                // @ts-expect-error Check for typing
                this.loggedIn = undefined;
                this.initSilentAuthDebounced();
            }
        }
    }
    handleActive() {
        if (this.active) {
            this.instanceActivated.emit({
                hash: this.hash
            });
            this.initSilentAuthDebounced();
        }
    }
    handleLoggedIn() {
        if (this.loggedIn && this._checkSessionEnabled) {
            this._startCheckSessionIntervalTimeout = window.setTimeout(this.startCheckSessionInterval, 100);
        }
        else {
            this.stopCheckSessionInterval();
        }
    }
    componentWillLoad() {
        this.active = false;
        this._authClientId = this.clientId;
        this.domElement.removeAttribute("client-id");
        this._authClient =
            typeof this.clientName === "string" && this.clientName.length > 0 ? getAuthClient(this.clientName) : getAuthClient();
        this._uid = getRandomString(32);
        this.hash = createHash([this._authClientId, this.clientName || "global"].join("-")).replace("-", "");
        if (this.redirectUri === undefined) {
            if (TRUMPF_ISSUERS_PROD.includes(this.issuer)) {
                this.redirectUri = TRUMPF_CALLBACK_URI_PROD;
            }
            else if (TRUMPF_ISSUERS_STAGE.includes(this.issuer)) {
                this.redirectUri = TRUMPF_CALLBACK_URI_STAGE;
            }
        }
        this._authTokenData = this._authClient.getTokenData();
        if (this._authTokenData !== null) {
            this._log("Reading token data from authClient");
        }
    }
    componentDidLoad() {
        this.activateDelegateDebounced();
        this.instanceAdded.emit({
            hash: this.hash,
            scope: this.scope
        });
    }
    disconnectedCallback() {
        this.stopCheckSessionInterval();
        this.cancelRefreshTokenTimeout();
    }
    activateDelegate = () => {
        this.findDelegatedInstance().active = true;
    };
    activateDelegateDebounced = debounce(this.activateDelegate, 100);
    initSilentAuth = async () => {
        this.cancelRefreshTokenTimeout();
        if (!this.active) {
            return;
        }
        this._log("Init silent auth");
        // @ts-expect-error Check for typing
        this.loggedIn = undefined;
        // @ts-expect-error Check for typing
        if (this._authTokenData !== null && this._authTokenData.refresh_token?.length > 0) {
            try {
                const res = await this.refreshToken();
                if (res?.access_token) {
                    return;
                }
            }
            catch {
                // Refreshing failed
            }
            this._log("Token refresh failed");
        }
        this._log("Request token via silent code flow");
        const payload = await this.initAuthPayload();
        this._authScopes = payload.scope.split(" ");
        this.initAuthCallback(this._authIframe);
        this._authIframe.src = `${this.issuer}/oauth2/authorize?${getQueryString(payload)}`;
        // Timeout if the auth iframe did not receive a message within 10 seconds (e.g. because redirection failed for some reason).
        window.clearTimeout(this._authFailureTimeout);
        this._authFailureTimeout = window.setTimeout(() => {
            if (this.loggedIn === undefined) {
                this.uxAuth.emit({ success: false, error: "timeout" });
            }
        }, this.failedTimeout * 1000);
    };
    initSilentAuthDebounced = debounce(this.initSilentAuth, 100);
    initAuthCallback = (iframe) => {
        if (typeof this._authCallbackHandler === "function") {
            window.removeEventListener("message", this._authCallbackHandler, true);
        }
        const authMessageHandler = (event) => {
            if (event.origin !== getUrlOrigin(this.redirectUri)) {
                return;
            }
            if (event.source !== iframe.contentWindow) {
                return;
            }
            window.removeEventListener("message", this._authCallbackHandler, true);
            const { code, error } = event.data;
            if (typeof code === "string") {
                this._log("Got one-time code");
                this._sessionState = event.data.session_state;
                this.requestToken(code);
                return;
            }
            if (typeof error === "string") {
                this._log(`Error: ${error}`);
                this._authClient.deleteAccessToken();
                this._authTokenData = null;
                this.loggedIn = false;
                this.uxAuth.emit({ success: false, error: error });
                return;
            }
        };
        this._authCallbackHandler = authMessageHandler.bind(this);
        window.addEventListener("message", this._authCallbackHandler, true);
    };
    findDelegatedInstance = () => {
        return (Array.from(document.querySelectorAll(`ux-u-silent-auth[hash="${this.hash}"]`)).reduce(
        // @ts-expect-error Check for typing
        (_previousValue, currentValue) => {
            if (currentValue.active) {
                return currentValue;
            }
        }) || document.querySelector(`ux-u-silent-auth[hash="${this.hash}"]`));
    };
    collectScopes = () => {
        const collectedScopes = this.scope.split(" ");
        Array.from(document.querySelectorAll(`ux-u-silent-auth[hash="${this.hash}"]`)).forEach((el) => {
            el.scope.split(" ").forEach((elScope) => {
                if (!collectedScopes.includes(elScope)) {
                    collectedScopes.push(elScope);
                }
            });
        });
        return collectedScopes.sort().join(" ");
    };
    initAuthPayload = async () => {
        this._codeVerifier = getCodeVerifier();
        const codeChallenge = await getCodeChallenge(this._codeVerifier);
        return {
            client_id: this._authClientId,
            code_challenge: codeChallenge,
            code_challenge_method: "S256",
            prompt: "none",
            redirect_uri: this.redirectUri,
            response_type: "code",
            scope: this.collectScopes(),
            // @ts-expect-error Check for typing
            state: window.top.location.href
        };
    };
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    requestToken = async (code) => {
        this._log("Request token with authorization_code grant");
        return this.sendTokenRequest({
            grant_type: "authorization_code",
            response_type: "token",
            redirect_uri: this.redirectUri,
            client_id: this._authClientId,
            scope: this.scope,
            code_verifier: this._codeVerifier,
            code
        });
    };
    refreshToken = () => {
        this._log("Request token with refresh_token grant");
        return this.sendTokenRequest({
            grant_type: "refresh_token",
            client_id: this._authClientId,
            // @ts-expect-error Check for typing
            refresh_token: this._authTokenData.refresh_token
        });
    };
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    sendTokenRequest = (data) => {
        return fetch(`${this.issuer}/oauth2/token`, {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            redirect: "follow",
            body: getQueryString(data)
        })
            .then((response) => response.json())
            .then((data) => {
            this._log("Got access token");
            this.handleTokenResponse(data);
            return data;
        });
    };
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    handleTokenResponse = (data) => {
        if (typeof data === "object" && typeof data.access_token === "string") {
            this._authTokenData = data;
            if (this._authTokenData.refresh_token && this._authTokenData.expires_in > 0) {
                const delay = Math.round(0.75 * this._authTokenData.expires_in);
                this._refreshTokenTimeout = window.setTimeout(this.initSilentAuth, delay * 1000);
            }
            this.loggedIn = true;
            // FIXME: We need the timeout as the access_token is not instantly valid in all scenarios
            window.setTimeout(() => {
                // @ts-expect-error Check for typing
                this._authClient.setTokenData(this._authTokenData);
                this.uxAuth.emit({ success: true, error: null });
            }, 2500);
        }
        else {
            this._authTokenData = null;
            this.loggedIn = false;
        }
    };
    cancelRefreshTokenTimeout = () => {
        window.clearTimeout(this._refreshTokenTimeout);
    };
    startCheckSessionInterval = () => {
        this.stopCheckSessionInterval();
        if (!this.active || !this.loggedIn) {
            return;
        }
        if (!(typeof this._rpIframe?.contentWindow !== "undefined")) {
            return;
        }
        this._log("Starting check-session interval");
        // @ts-expect-error Check for typing
        this._rpIframe.contentWindow.addEventListener("message", this.handleCheckSessionMessage, true);
        this._rpIframeInterval = window.setInterval(() => {
            try {
                /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
                this._rpIframe.contentWindow.poll();
            }
            catch (e) {
                /* eslint-disable-next-line no-console */
                console.error(e);
                this.stopCheckSessionInterval();
            }
        }, 2000);
    };
    stopCheckSessionInterval = () => {
        window.clearTimeout(this._startCheckSessionIntervalTimeout);
        window.clearInterval(this._rpIframeInterval);
    };
    handleCheckSessionMessage = (event) => {
        switch (event.data) {
            case "unchanged":
                break;
            case "changed":
                this._log("Session changed");
                this.stopCheckSessionInterval();
                // @ts-expect-error Check for typing
                this.loggedIn = undefined;
                this.initSilentAuth();
                break;
            case "error":
                this.stopCheckSessionInterval();
                // @ts-expect-error Check for typing
                this.loggedIn = undefined;
                this._authClient.deleteAccessToken();
                break;
            default:
                break;
        }
    };
    render() {
        if (!this.active) {
            return h(Host, { class: "ux-u-silent-auth" });
        }
        let opIframeSrc = "";
        let rpIframeSrcDoc = "";
        if (this._checkSessionEnabled && this.loggedIn) {
            opIframeSrc = `${this.issuer}/oidc/checksession?${getQueryString({
                client_id: this._authClientId,
                redirect_uri: this.redirectUri
            })}`;
            rpIframeSrcDoc = getRPIframeSrcdoc({
                issuer: this.issuer,
                clientId: this._authClientId,
                sessionState: this._sessionState
            }, `OP-${this._uid}`);
        }
        return (h(Host, { class: "ux-u-silent-auth" }, h("iframe", { ref: (el) => (this._authIframe = el) }), this._checkSessionEnabled && this.loggedIn && (h("div", null, h("iframe", { id: `RP-${this._uid}`, ref: (el) => (this._rpIframe = el), srcDoc: rpIframeSrcDoc }), h("iframe", { id: `OP-${this._uid}`, src: opIframeSrc })))));
    }
    static get is() { return "ux-u-silent-auth"; }
    static get originalStyleUrls() {
        return {
            "$": ["silent-auth.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["silent-auth.css"]
        };
    }
    static get properties() {
        return {
            "issuer": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "URL of the identity provider"
                },
                "attribute": "issuer",
                "reflect": false,
                "defaultValue": "\"https://identity.trumpf.com\""
            },
            "clientId": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Auth client id (is hidden when the component mounts)"
                },
                "attribute": "client-id",
                "reflect": false
            },
            "clientName": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Name of the client, used to get the scoped AuthClient instance"
                },
                "attribute": "client-name",
                "reflect": false
            },
            "scope": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Requested scopes"
                },
                "attribute": "scope",
                "reflect": false,
                "defaultValue": "\"oicd\""
            },
            "redirectUri": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Auth callback URL. Only needs to be provided for issuers other than https://identity*.trumpf.com"
                },
                "attribute": "redirect-uri",
                "reflect": false
            },
            "failedTimeout": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Time in seconds after which the authentication flow is considered as failed"
                },
                "attribute": "failed-timeout",
                "reflect": false,
                "defaultValue": "10"
            },
            "debug": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "If set to true, infos are logged to the console"
                },
                "attribute": "debug",
                "reflect": false
            },
            "hash": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Automatically generated hash used to find related components in the DOM tree"
                },
                "attribute": "hash",
                "reflect": true
            },
            "active": {
                "type": "boolean",
                "mutable": true,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Automatically set flag that indicates whether this instance is commissioned to perform authentication"
                },
                "attribute": "active",
                "reflect": true
            }
        };
    }
    static get states() {
        return {
            "loggedIn": {}
        };
    }
    static get events() {
        return [{
                "method": "instanceAdded",
                "name": "uxSilentAuthAdded",
                "bubbles": true,
                "cancelable": false,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Emitted when the component is loaded to inform the delegated instance about scopes that may have changed"
                },
                "complexType": {
                    "original": "{\n        hash: string;\n        scope: string;\n    }",
                    "resolved": "{ hash: string; scope: string; }",
                    "references": {}
                }
            }, {
                "method": "instanceActivated",
                "name": "uxSilentAuthActivated",
                "bubbles": true,
                "cancelable": false,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Emitted when the component is activated"
                },
                "complexType": {
                    "original": "{\n        hash: string;\n    }",
                    "resolved": "{ hash: string; }",
                    "references": {}
                }
            }, {
                "method": "uxAuth",
                "name": "uxAuth",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Emitted after the authentication flow was completed.\nAt this point either a token was fetched successfully and broadcasted to the `AuthClient`,\nor silent authentication failed for some reason."
                },
                "complexType": {
                    "original": "{\n        success: boolean;\n        error: string | null;\n    }",
                    "resolved": "{ success: boolean; error: string | null; }",
                    "references": {}
                }
            }];
    }
    static get elementRef() { return "domElement"; }
    static get watchers() {
        return [{
                "propName": "active",
                "methodName": "handleActive"
            }, {
                "propName": "loggedIn",
                "methodName": "handleLoggedIn"
            }];
    }
    static get listeners() {
        return [{
                "name": "uxSilentAuthAdded",
                "method": "onClientAdded",
                "target": "window",
                "capture": false,
                "passive": false
            }];
    }
}
//# sourceMappingURL=silent-auth.js.map
